import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, ImagePlus, Stamp } from "lucide-react";
import { CameraCapture } from "@/components/CameraCapture";
import { stampImageFile } from "@/lib/media";

// upload: async (File) => { id }   |   onUploaded: (ids[]) => void
export function MediaUploader({ upload, onUploaded, allowPdf = true }) {
  const fileRef = useRef();
  const [cam, setCam] = useState(false);
  const [stamp, setStamp] = useState(true);
  const [busy, setBusy] = useState(false);

  const run = async (files, { skipStamp = false } = {}) => {
    if (!files.length) return;
    setBusy(true);
    const ids = [];
    let failures = 0;
    for (let f of files) {
      try {
        if (!skipStamp && stamp && f.type.startsWith("image/")) {
          try { f = await stampImageFile(f); } catch (e) { console.warn("Timestamp stamp failed, using original image:", e); }
        }
        const res = await upload(f);
        if (res?.id) ids.push(res.id);
      } catch {
        failures += 1;
      }
    }
    setBusy(false);
    if (ids.length) { onUploaded(ids); toast.success(`${ids.length} file${ids.length > 1 ? "s" : ""} added`); }
    if (failures) toast.error(`${failures} file${failures > 1 ? "s" : ""} failed to upload`);
  };

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    run(files);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      <input ref={fileRef} data-testid="media-file-input" type="file" multiple accept={allowPdf ? "image/*,application/pdf" : "image/*"} onChange={onPick} className="hidden" />
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" data-testid="media-upload-btn" onClick={() => fileRef.current?.click()} disabled={busy}
          className="flex items-center gap-1.5 bg-zinc-950 border-2 border-zinc-700 hover:border-[#FF5722] text-zinc-200 font-head font-bold uppercase text-xs px-3 py-2 transition-colors disabled:opacity-50">
          <ImagePlus size={15} /> {busy ? "Uploading…" : "Upload"}
        </button>
        <button type="button" data-testid="media-camera-btn" onClick={() => setCam(true)} disabled={busy}
          className="flex items-center gap-1.5 bg-[#FF5722] text-black border-2 border-black shadow-[3px_3px_0px_#000] font-head font-bold uppercase text-xs px-3 py-2 disabled:opacity-50">
          <Camera size={15} /> Camera
        </button>
        <button type="button" data-testid="media-stamp-toggle" onClick={() => setStamp((s) => !s)}
          className={`flex items-center gap-1.5 border-2 font-head font-bold uppercase text-xs px-3 py-2 transition-colors ${stamp ? "border-[#FF5722] text-[#FF5722]" : "border-zinc-700 text-zinc-500"}`}>
          <Stamp size={15} /> Timestamp: {stamp ? "On" : "Off"}
        </button>
      </div>
      <CameraCapture open={cam} onClose={() => setCam(false)} stamp={stamp} onCapture={(files) => run(files, { skipStamp: true })} />
    </div>
  );
}
